import { Observable } from 'rxjs';

import { ApiResponseModel } from '~app/models/responses/api-response.model';
import { BaseSearchStore } from '~core/abstractions/base-search.store';
import { Identifyable } from '~core/abstractions/identifyable';
import { TreeItemFlatNode } from '~core/models/tree-item.flat-node';
import { OptionalType } from '~shared/core/types/optional.type';

export class TreeDataSelectionConfig<TModel extends Identifyable<TId>, TId = number> {
    getNodeTitle!: (item: TModel) => string;
    search!: (searchText: string) => Observable<TModel[]>;

    loadRootItems!: () => Observable<TModel[]>;
    loadChildren!: (parentId: TId) => Observable<TModel[]>;

    getNodeParent!: (node: TModel) => OptionalType<TModel>;
    hasNodeChildren!: (node: TModel) => boolean;

    store!: BaseSearchStore<TModel, TId>;

    /**
     * If true, allows the user to create a new record inline.
     * Default: true.
     */
    allowsInlineCreation = true;

    /**
     * Define which level(s) are selectable.
     * 0 is the root level.
     * Default: [2].
     */
    allowSelectionAtLevels: number[] = [2];

    /**
     * The deepest possible level for this tree.
     */
    deepestLevel = 2;

    /**
     * If true, allows the user to edit the records.
     */
    allowGroupEditing = true;

    /**
     * The title key of the search & selection dialog.
     */
    selectionDialogTitleKey!: string;

    /**
     * Whether the user is allowed to select the empty option - no option selected.
     */
    allowEmptySelection = false;
    addItemAtLevelZeroTextKey = 'General.Actions.Create';
    addItemAtLevelOneTextKey = 'General.Actions.Create';
    addItemAtLevelTwoTextKey = 'General.Actions.Create';
    saveNewRecordHandler?: (node: any, text: string) => Observable<ApiResponseModel<any>>;

    constructor(init: Partial<TreeDataSelectionConfig<TModel, TId>>) {
        if (!!init) {
            Object.assign(this, init);
        }
    }

    getById(id: TId): Observable<ApiResponseModel<TModel>> {
        return this.store.getListModel(id);
    }

    /**
     * Maps children items to TreeItemFlatNode format
     * @param children Children items to map
     * @param childLevel Level of the children
     * @returns Mapped TreeItemFlatNode array
     */
    mapChildren(children: TModel[], childLevel: number): TreeItemFlatNode<TId>[] {
        return children.map<TreeItemFlatNode<TId>>(item => ({
            id: item.id,
            text: this.getNodeTitle(item),
            level: childLevel,
            isExpandable: this.hasNodeChildren(item) || childLevel < this.deepestLevel
        }));
    }
}
